(() => {
  const selector = '[data-rel-transformation]';

  class RelatableTransformation {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-slider-track]');
      this.previous = root.querySelector('[data-slider-previous]');
      this.next = root.querySelector('[data-slider-next]');
      this.controls = root.querySelector('[data-slider-controls]');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = root.dataset.mobileAutoplay === 'true';
      this.autoplayDelay = Number(root.dataset.autoplayDelay) || 4000;
      this.visible = true;
      this.programmaticScroll = false;
      this.autoplayTimer = null;
      this.resumeTimer = null;
      this.scrollFrame = null;
      this.controller = new AbortController();
      this.signal = this.controller.signal;

      if (!this.track || !this.previous || !this.next || !this.controls) return;

      this.bindEvents();
      this.observe();
      this.refresh();
    }

    bindEvents() {
      this.previous.addEventListener('click', () => this.move(-1), { signal: this.signal });
      this.next.addEventListener('click', () => this.move(1), { signal: this.signal });
      this.track.addEventListener('pointerdown', () => this.pause(), { signal: this.signal });
      this.track.addEventListener('pointerup', () => this.resumeAfterInteraction(), { signal: this.signal });
      this.track.addEventListener('pointercancel', () => this.resumeAfterInteraction(), { signal: this.signal });
      this.track.addEventListener('keydown', () => this.pauseTemporarily(), { signal: this.signal });
      this.track.addEventListener('wheel', (event) => this.onWheel(event), { passive: false, signal: this.signal });
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.signal });
      document.addEventListener('visibilitychange', () => this.refreshAutoplay(), { signal: this.signal });
      this.addMediaListener(this.mobileQuery, () => this.refresh());
      this.addMediaListener(this.reducedMotionQuery, () => this.refreshAutoplay());
    }

    addMediaListener(query, handler) {
      if (query.addEventListener) {
        query.addEventListener('change', handler, { signal: this.signal });
      } else {
        query.addListener(handler);
        this.signal.addEventListener('abort', () => query.removeListener(handler), { once: true });
      }
    }

    observe() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.refresh(), { signal: this.signal });
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.35;
            this.refreshAutoplay();
          },
          { threshold: [0, 0.35] }
        );
        this.intersectionObserver.observe(this.root);
      }
    }

    refresh() {
      const overflows = this.track.scrollWidth - this.track.clientWidth > 2;
      this.root.dataset.sliderOverflow = String(overflows);
      this.controls.hidden = !overflows;
      this.updateControls();
      this.refreshAutoplay();
    }

    updateControls() {
      const tolerance = 4;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      this.previous.disabled = this.track.scrollLeft <= tolerance;
      this.next.disabled = this.track.scrollLeft >= maximum - tolerance;
    }

    slideDistance() {
      const slide = this.track.querySelector('[data-slider-slide]');
      if (!slide) return this.track.clientWidth;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    move(direction) {
      this.pauseTemporarily();
      this.scrollBy(direction * this.slideDistance());
    }

    scrollBy(distance) {
      this.programmaticScroll = true;
      this.track.scrollBy({
        left: distance,
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth'
      });
      window.setTimeout(() => {
        this.programmaticScroll = false;
      }, 700);
    }

    onWheel(event) {
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      const canMoveForward = delta > 0 && this.track.scrollLeft < maximum - 2;
      const canMoveBackward = delta < 0 && this.track.scrollLeft > 2;

      if (!canMoveForward && !canMoveBackward) return;

      event.preventDefault();
      this.pauseTemporarily();
      this.track.scrollLeft += delta;
    }

    onScroll() {
      if (!this.programmaticScroll) this.pauseTemporarily();
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.updateControls();
        this.scrollFrame = null;
      });
    }

    pause() {
      window.clearTimeout(this.autoplayTimer);
      window.clearTimeout(this.resumeTimer);
      this.autoplayTimer = null;
      this.resumeTimer = null;
    }

    pauseTemporarily() {
      this.pause();
      this.resumeAfterInteraction();
    }

    resumeAfterInteraction() {
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.refreshAutoplay(), Math.max(3000, this.autoplayDelay));
    }

    canAutoplay() {
      return this.autoplayEnabled &&
        this.mobileQuery.matches &&
        !this.reducedMotionQuery.matches &&
        this.visible &&
        !document.hidden &&
        this.root.dataset.sliderOverflow === 'true';
    }

    refreshAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      if (!this.canAutoplay()) return;
      this.autoplayTimer = window.setTimeout(() => this.autoplay(), this.autoplayDelay);
    }

    autoplay() {
      if (!this.canAutoplay()) return;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      const atEnd = this.track.scrollLeft >= maximum - 4;

      this.programmaticScroll = true;
      this.track.scrollTo({
        left: atEnd ? 0 : Math.min(this.track.scrollLeft + this.slideDistance(), maximum),
        behavior: 'smooth'
      });
      window.setTimeout(() => {
        this.programmaticScroll = false;
        this.refreshAutoplay();
      }, 700);
    }

    destroy() {
      this.pause();
      this.controller.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      delete this.root.relTransformationInstance;
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(selector) ? [scope] : scope.querySelectorAll(selector);
    roots.forEach((root) => {
      if (root.relTransformationInstance) return;
      root.relTransformationInstance = new RelatableTransformation(root);
    });
  };

  if (!window.RelatableTransformationManager) {
    window.RelatableTransformationManager = { initialize };
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.matches?.(selector) ? event.target : event.target.querySelector(selector);
      root?.relTransformationInstance?.destroy();
    });
  }

  window.RelatableTransformationManager.initialize();
})();
